import React, { useState, useEffect } from 'react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { formatShortDate } from '../../utils/date-utils';

export default function DayNoteEditor({ 
  isOpen, 
  onClose, 
  onSave,
  date,
  initialNote = null
}) {
  const [noteText, setNoteText] = useState('');
  const [audience, setAudience] = useState('internal');
  const [includeInEmail, setIncludeInEmail] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isOpen && initialNote) {
      setNoteText(initialNote.text || '');
      setAudience(initialNote.audience || 'internal');
      setIncludeInEmail(initialNote.includeIn4amEmail !== false);
    } else if (isOpen) {
      setNoteText('');
      setAudience('internal');
      setIncludeInEmail(true);
    }
  }, [isOpen, initialNote]);

  async function handleSave() {
    setSaving(true);

    try {
      await onSave({
        text: noteText.trim(),
        audience: audience,
        includeIn4amEmail: includeInEmail,
        date: date
      });
      onClose();
    } catch (error) {
      console.error('Error saving note:', error);
    } finally {
      setSaving(false);
    }
  }

  function handleDelete() {
    onSave(null); // Pass null to delete
    onClose();
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Day Note - ${date ? formatShortDate(date) : ''}`}
      size="medium"
    >
      <div style={{ padding: '24px' }}>
        {/* Note Text */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '6px'
          }}>
            Note
          </label>
          <textarea
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder="Add a note for this day..."
            rows={6}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              fontFamily: 'inherit',
              resize: 'vertical'
            }}
            onFocus={(e) => e.target.style.borderColor = '#8B4513'}
            onBlur={(e) => e.target.style.borderColor = '#ddd'}
          />
        </div>

        {/* Audience */}
        <div style={{ marginBottom: '20px' }}>
          <label style={{
            display: 'block',
            fontSize: '13px',
            fontWeight: '600',
            color: '#2b2b2b',
            marginBottom: '6px'
          }}>
            Audience
          </label>
          <select
            value={audience}
            onChange={(e) => setAudience(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px',
              border: '1px solid #ddd',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              background: 'white'
            }}
            onFocus={(e) => e.target.style.borderColor = '#8B4513'}
            onBlur={(e) => e.target.style.borderColor = '#ddd'}
          >
            <option value="internal">Internal (Staff Only)</option>
            <option value="customer">Customer Visible</option>
          </select>
        </div>

        {/* Include in 4am Email */}
        {audience === 'internal' && (
          <div style={{ marginBottom: '20px' }}>
            <label style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              cursor: 'pointer'
            }}>
              <input
                type="checkbox"
                checked={includeInEmail}
                onChange={(e) => setIncludeInEmail(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer'
                }}
              />
              <span style={{
                fontSize: '14px',
                color: '#2b2b2b'
              }}>
                Include in 4am daily email
              </span>
            </label>
          </div>
        )}
      </div>

      {/* Footer Actions */}
      <div style={{
        padding: '16px 24px',
        borderTop: '1px solid #e5e5e5',
        display: 'flex',
        justifyContent: 'space-between',
        background: '#fafafa'
      }}>
        <div>
          {initialNote && (
            <Button
              variant="secondary"
              onClick={handleDelete}
              disabled={saving}
              style={{ color: '#dc2626' }}
            >
              Delete Note
            </Button>
          )}
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <Button
            variant="secondary"
            onClick={onClose}
            disabled={saving}
          >
            Cancel
          </Button>
          
          <Button
            onClick={handleSave}
            disabled={saving || !noteText.trim()}
          >
            {saving ? 'Saving...' : 'Save Note'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}